import compareDocumentItems from 'builder/Knowledge/components/TreeView/compareDocumentItems';
import {
  regexFindDoubleQuotes,
  competencyTitleKeyPrefixDelimitor,
  escapeDoubleQuotation,
  regexFindDot,
  knowledgeMapDetailInputStates,
  competencyDocumentItemPrefixes,
  competencyDocumentItemSufixes,
} from 'constants/knowledgeMap';
import { 
  competencyDocumentItemAssociationDeleteMutation,
  competencyDocumentItemAssociationMutation,
  competencyDocumentItemUpdateMutation,
} from 'Workspace/Knowledge/utils/graphqlMutation';
import { 
  deleteKnowledgeMapItemAssociation,
  createKnowledgeMapItemAssociation,
  updateKnowledgeMapItem,
} from 'Workspace/Knowledge/services';

const {  
  COMPETENCY_DOCUMENT_ITEM_PARENT_KEY,
  COMPETENCY_DOCUMENT_ITEM_CHILD_KEY,
} = competencyDocumentItemPrefixes;

const { UNDER_SCORE } = competencyDocumentItemSufixes;
const { TITLEKEY , ABBREVIATEDSTATEMENT } = knowledgeMapDetailInputStates;

/**
 * update knowledge map items
 * 
 * @param {Array} competencyItems
 * @param {String} knowledgeMapId
 * @param {Object} apolloClient - apollo client current instance.
 * @returns {Object} response of createKnowledgeMapItem
 */
export const updateCompetencyDocumentItems = async (competencyItems, knowledgeMapId, apolloClient) => {
  
  const knowledgeMapItemsUpdateQuery = Object.keys(competencyItems).reduce( (knowledgeMapItemUpdateQuery, knowledgeMapKey) => {
    
    let {abbreviatedStatement, fullStatement, humanCodingScheme, titleKey, id} = competencyItems[knowledgeMapKey];
    
    titleKey = titleKey.replace(regexFindDot, competencyTitleKeyPrefixDelimitor);
    abbreviatedStatement = abbreviatedStatement.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
    fullStatement = fullStatement.replace(regexFindDoubleQuotes, escapeDoubleQuotation);
    humanCodingScheme = humanCodingScheme.replace(regexFindDoubleQuotes, escapeDoubleQuotation);

    knowledgeMapItemUpdateQuery += competencyDocumentItemUpdateMutation(
      titleKey, 
      id,
      knowledgeMapId,
      abbreviatedStatement, 
      fullStatement, 
      humanCodingScheme,
    );
  
    return knowledgeMapItemUpdateQuery;
  }, '');
  return await updateKnowledgeMapItem(knowledgeMapItemsUpdateQuery, apolloClient, knowledgeMapId);
};


/**
 * Generate the Competency Document item list to be deleted
 * 
 * @param {Object} sourceCompetencyDocumentItem 
 * @param {Object} destinationCompetencyDocumentItem 
 * @param {Number} prefixDecimalNumber 
 * @param {Class} competencyTree 
 * @param {Object} competencyDocumentItemResult 
 * @param {Object} apolloClient 
 * 
 */
export const generateCompetencyDocumentToBeUpdated = async (inputArguments, apolloClient) => {

  const {
    sourceCompetencyDocumentItem,
    destinationCompetencyDocumentItem,
    prefixDecimalNumber,
    competencyTree,
    competencyDocumentItemResult,
    destinationItemAlias,
  } = inputArguments;

  const { 
    id: knowledgeMapItemId,
    documentId: knowledgeMapId,
  } = sourceCompetencyDocumentItem;
  
  const competencyDocumentRootItems = competencyTree.getRootItems().sort(compareDocumentItems);
  
  const isParentItem = competencyDocumentRootItems.find((rootItem) => rootItem.id === knowledgeMapItemId);
  
  if(isParentItem) {
    competencyDocumentItemResult[TITLEKEY] = `${ COMPETENCY_DOCUMENT_ITEM_PARENT_KEY }${ prefixDecimalNumber }`;
    competencyDocumentItemResult[ABBREVIATEDSTATEMENT] = `${competencyDocumentItemResult[TITLEKEY]}-${competencyDocumentItemResult[ABBREVIATEDSTATEMENT]}`;
  }

  const childItems = competencyTree.getChildren(sourceCompetencyDocumentItem.id).sort(compareDocumentItems);

  if(childItems && childItems.length > 0) {

    updateChildItems(childItems, prefixDecimalNumber);

  } else if(!isParentItem) {
    let {
      destinationParentId,
    } = inputArguments;
    const { id: destinationKnowledgeMapItemId } = destinationCompetencyDocumentItem;
    const [ parentItemId ] = competencyTree.getAllParentsByDeepChildId(destinationKnowledgeMapItemId);
    if(destinationParentId === ''){
      [ destinationParentId ] = competencyTree.getAllParentsByDeepChildId(destinationKnowledgeMapItemId);
    }
    const parentItem = competencyTree.itemsById[parentItemId];
    const childKeyWithPrefixDecimal = `${ COMPETENCY_DOCUMENT_ITEM_CHILD_KEY }${prefixDecimalNumber}`;

    competencyDocumentItemResult[TITLEKEY] = `${destinationItemAlias?destinationItemAlias.split('_')[0]:parentItem[TITLEKEY]}${ UNDER_SCORE }${ childKeyWithPrefixDecimal }`;
    competencyDocumentItemResult[ABBREVIATEDSTATEMENT] = `${competencyDocumentItemResult[TITLEKEY]}-${competencyDocumentItemResult[ABBREVIATEDSTATEMENT]}`;

    if(parentItemId !== knowledgeMapId){

      await updateAssociationOfChildItems(sourceCompetencyDocumentItem, knowledgeMapId, knowledgeMapItemId, destinationParentId, apolloClient );

    }
  }
    
  const competencyDocumentsTobeUpdated = [competencyDocumentItemResult].concat(childItems);

  updateCompetencyDocumentItems(competencyDocumentsTobeUpdated, knowledgeMapId, apolloClient);
};

/**
 * 
 * @param {Array} childItems 
 * @param {Number} prefixDecimalNumber 
 * @returns {Array} updated ChildItems
 */

export const updateChildItems = (childItems, prefixDecimalNumber) => {
  childItems.map( (childItem, index) => {
    const parentKeyWithPrefixDecimal = `${ COMPETENCY_DOCUMENT_ITEM_PARENT_KEY }${ prefixDecimalNumber }${ UNDER_SCORE }`;
    childItems[index][TITLEKEY] = `${ parentKeyWithPrefixDecimal }${ COMPETENCY_DOCUMENT_ITEM_CHILD_KEY }${ index + 1 }`;
    childItems[index][ABBREVIATEDSTATEMENT] = `${childItems[index][TITLEKEY]}-${childItem[ABBREVIATEDSTATEMENT]}`;
  });
  return childItems;
};

/**
 * 
 * @param {Object} sourceCompetencyDocumentItem 
 * @param {String} knowledgeMapId 
 * @param {String} knowledgeMapItemId 
 * @param {String} destinationParentItemId 
 * @param {Object} apolloClient 
 */

export const updateAssociationOfChildItems = async (sourceCompetencyDocumentItem, knowledgeMapId, knowledgeMapItemId, destinationParentItemId, apolloClient ) => {
  const {
    id:associationIdToBeDeleted,
    destinationItemId:associationIdToBeDeletedFrom,
  } = sourceCompetencyDocumentItem.associationsTo[0];

  const associationObject = { 
    [associationIdToBeDeleted]: associationIdToBeDeletedFrom,
    [associationIdToBeDeletedFrom]: knowledgeMapItemId,
  };

  const competencyDocumentItemAssociationDeleteQuery = competencyDocumentItemAssociationDeleteMutation(COMPETENCY_DOCUMENT_ITEM_CHILD_KEY, associationIdToBeDeleted, knowledgeMapId);
  await deleteKnowledgeMapItemAssociation(competencyDocumentItemAssociationDeleteQuery, apolloClient, knowledgeMapId, associationObject);

  const competencyDocumentItemAssociationQuery = competencyDocumentItemAssociationMutation(COMPETENCY_DOCUMENT_ITEM_CHILD_KEY, knowledgeMapItemId, destinationParentItemId, knowledgeMapId);
  await createKnowledgeMapItemAssociation(competencyDocumentItemAssociationQuery, apolloClient, knowledgeMapId);
  
};